import { createMemoryHistory } from "history"
import { useEffect, useMemo } from "react"
import { useHistory } from "react-router-dom"
import { StringParam, useQueryParams } from "use-query-params"

export type UseQueryParamStateReturn = ReturnType<typeof useQueryParamState>

export function useQueryParamState(queryParamName: string) {
  // Using useQueryParams in order to manage the query parameter better
  // (than if using useQueryParam) since we can pass undefined to remove the query param.
  const [queryParams, setQueryParams] = useQueryParams({
    [queryParamName]: StringParam
  })
  const queryParamPath = queryParams[queryParamName] || null

  // Passing undefined removes the query param.
  function setQueryParamPath(path?: string) {
    setQueryParams({
      [queryParamName]: path
    })
  }

  return {
    queryParamPath,
    setQueryParamPath
  }
}

export type UseQueryParamHistoryReturn = ReturnType<typeof useQueryParamHistory>

function useQueryParamHistory(queryParamName: string) {
  const rootHistory = useHistory()
  // Using useQueryParams in order to manage the query parameter better
  // (than if using useQueryParam) since we can pass undefined to remove the query param.
  const { queryParamPath } = useQueryParamState(queryParamName)

  // Create history with initial entries
  const history = useMemo(
    () =>
      createMemoryHistory({
        initialEntries: queryParamPath ? [queryParamPath] : ["/"],
        initialIndex: 0
      }),
    []
  )

  // Listen to history changes and update query param
  useEffect(() => {
    const unlisten = history.listen((location) => {
      const nextQueryParamPath = location.pathname + location.search
      if (nextQueryParamPath && nextQueryParamPath !== "/") {
        // Rather than using setQueryParamPath(location.pathname), we're using the root history
        // which (interestingly) allows for better native navigation (like browser back/forward buttons).
        const url = new URL(window.location.href)
        url.searchParams.set(queryParamName, nextQueryParamPath)
        // Setting updated query param in current url, navigating to it.
        rootHistory.replace({
          pathname: url.pathname,
          search: url.search
        })
      }
    })

    return unlisten
  }, [])

  // Update memory router when query param changes externally
  useEffect(() => {
    if (queryParamPath) {
      // Check if the current path is different from what we want
      if (history.location?.pathname !== queryParamPath) {
        // Only replace history if current path is not "/"
        history.replace(queryParamPath)
      }
    }
  }, [queryParamPath])

  return history
}

export default useQueryParamHistory
